import React, {CSSProperties, useEffect, useState} from "react";
import {generateClassName, ObjTest, treeUtil} from "jzfz-web-utils";
import {ModuleEnum} from "../../config/constant";
import {getArticleInfo} from '../../api/commonAPI';
import {Spin, Tooltip, Tree} from "antd";
import {MultipleBookIcon} from "../icon/Icon";
import ExpandButton from "../expandButton/ExpandButton";
import CustomEmpty from "../customEmpty/CustomEmpty";
import './style/knowledgeGroup.less';


const { getListFromTree } = treeUtil;


export interface KnowledgeGroupData {
	articleList?: any[];	// 国家规范
	localStandard?: any[];	// 本地标准
	otherStandard?: any[];	// 其它标准
	referenceBooks?: any[];	// 参考书籍
	mapPageList?: any[];	// 标准图集
	materialList?: any[];	// 学习资料
	problemList?: any[];	// 典型问题
	questionList?: any[];	// 考试
	regulationList?: any[];	// 规定问题
	taskList?: any[];	// 标准任务
}


export interface KnowledgeGroupProps {
	className?: string;
	style?: CSSProperties;
	dataSource?: KnowledgeGroupData;	// 数据源
	loading?: boolean;
	selectedKeys?: string[];	// 选中的节点的key
	defaultExpandAll?: boolean;	// 默认展开所有树节点
	onSelect?: (nodeInfo: any) => void;	// data 选中的知识的信息
	onLoad?: (treeData: any[]) => void;
}


/**
 * 知识分组组件
 * @param props
 * @constructor
 */
const KnowledgeGroup = (props: KnowledgeGroupProps) => {
	const {
		className,
		style,
		dataSource,
		loading = false,
		selectedKeys,
		defaultExpandAll,
		onSelect = (nodeInfo: any) => {},
		onLoad = (treeData: any[]) => {},
	} = props;
	const [data, setData] = useState<any[]>([]);
	const [expandedKeys, setExpandedKeys] = useState<any[]>([]);	// 展开节点的key
	const [expandAll, setExpandAll] = useState<boolean>(false); // 知识弹窗展开收起按钮


	useEffect(() => {
		const tempList = buildData(dataSource);
		setData(tempList);
		onLoad(tempList);
		// 学习资料全部展开
		if (defaultExpandAll) {
			expandHandle(true, tempList);
		}
	}, [dataSource])


	/**
	 * 构造数据
	 * @param data
	 */
	const buildData = (data: any): any[] => {
		if (!data || Object.keys(data).length === 0) {
			return [];
		}

		// 国家规范、 本地规范、其它规范、参考书籍分组
		const { articleList = [], localStandard = [], otherStandard = [], referenceBooks = [],
			mapPageList = [], regulationList = [], problemList = [], materialList = [],
		} = data;
		let standardTree = {
			type: ModuleEnum.standard,
			key: 'articleList',
			title: '国家规范',
			children: groupBy(
				articleList,
				'standardPkid',
				'articleCode',
				ModuleEnum.standard,
				(node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)
			),
		};
		let localStandardTree = {
			type: ModuleEnum.standard,
			key: 'localStandard',
			title: '本地规范',
			children: groupBy(
				localStandard,
				'standardPkid',
				'articleCode',
				ModuleEnum.standard,
				(node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)
			),
		};
		let otherStandardTree = {
			type: ModuleEnum.standard,
			key: 'otherStandard',
			title: '其它规范',
			children: groupBy(
				otherStandard,
				'standardPkid',
				'articleCode',
				ModuleEnum.standard,
				(node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)
			),
		};
		let referenceBooksTree = {
			type: ModuleEnum.standard,
			key: 'referenceBooks',
			title: '参考书籍',
			children: groupBy(
				referenceBooks,
				'standardPkid',
				'articleCode',
				ModuleEnum.standard,
				(node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)
			),
		};
		let standardMapTree = {
			type: ModuleEnum.standardMap,
			key: 'mapPageList',
			title: '图集',
			children: groupBy(
				mapPageList,
				'mapId',
				'pageCode',
				ModuleEnum.standardMap,
				(node: any) => (`${node.mapName} ${node.mapCode ? node.mapCode : ''}`)
			),
		};
		// 规定问题分组
		let regulationTree = {
			type: ModuleEnum.rules,
			key: 'regulationList',
			title: '规定问题',
			children: groupBy(
				mergePath(regulationList, ModuleEnum.rules),
				'unionId',
				'regulationCode',
				ModuleEnum.rules,
				(node: any) => node.unionPath
			),
		};
		// 典型问题
		let typicalList = {
			type: ModuleEnum.typical,
			key: 'problemList',
			title: '典型问题',
			children: problemList.map((v: any) => ({ ...v, key: v.pkid, title: `编号：${v.problemNumber}`, selectable: true, moduleType: ModuleEnum.typical })),
		};
		// 学习资料
		let learnDataTree = {
			type: ModuleEnum.learn,
			key: 'materialList',
			title: '学习资料',
			children: mergeTree(materialList, ModuleEnum.learn),
		}

		let tempList = [
			standardTree,
			localStandardTree,
			otherStandardTree,
			referenceBooksTree,
			regulationTree,
			typicalList,
			learnDataTree,
			standardMapTree,
		].filter((v: any) => ObjTest.isNotEmptyArray(v.children))

		return tempList;
	}


	/**
	 * 合并路径
	 * @param list
	 * @param type
	 */
	const mergePath = (list: any[], type: ModuleEnum): any[] => {
		return list.map((v: any) => {
			let tempPaths = v.navigatePath.filter((path: any) => {
				if (type === ModuleEnum.rules) {
					return path.pathType === 1;
				}
				if (type === ModuleEnum.learn) {
					return [1, 2].includes(path.pathType);
				}
				return false;
			});
			let unionId = tempPaths.map((path: any) => path.pkid).join('__');
			let unionPath = tempPaths.map((path: any) => path.pathName).join('/');
			let unionPaths = [{ pkid: unionId, pathName: unionPath }, ...v.navigatePath.filter((path: any) => path.pathType === 3)];
			return {
				...v,
				unionId,
				unionPath,
				unionPaths,
			}
		})
	}


	/**
	 * 生成树
	 * @param list
	 * @param id
	 */
	const getTree = (list: any[], id: string) => {
		let arr = [];
		for (let i = 0; i < list.length; i++) {
			let item = list[i];
			let className = ['flex1 flexic ofh'];
			if (item.pid === id) {
				item.key = item.pkid;
				item.title = (<span title={item.pathName} className="flex1 flexic ofh"><span className="eps">{item.pathName}</span>{getMultipleIcon(item, ModuleEnum.learn)}</span>);
				item.moduleType = ModuleEnum.learn;
				if (!item.selectable) {
					className.push('tree-node-unselect');
				}
				item.className = generateClassName(className);
				item.children = getTree(list, item.pkid);
				arr.push(item);
			}
		}

		return arr;
	}


	/**
	 * 合并树
	 * @param list
	 */
	const mergeTree = (list: any[], type: ModuleEnum): any[] => {
		let tempList = mergePath(list, type).map(v => {
			return {
				...v,
				unionPaths: v.unionPaths.map((item, i) => ({ ...item, pid: i === 0 ? null : v.unionPaths[i - 1].pkid }))
			}
		})

		let objs = tempList.reduce((p, c) => [...p, ...c.unionPaths], []);

		// 去重
		let keys = [];
		let newObjs = [];
		objs.forEach(v => {
			if (!keys.includes(v.pkid)) {
				keys.push(v.pkid);
				newObjs.push(v);
			}
		})

		newObjs = newObjs.map((v: any) => {
			let target = list.find((item: any) => v.pkid === item.pkid);
			if (target) {
				return { ...v, ...target, selectable: true };
			}
			return v;
		})

		return getTree(newObjs, null);
	}


	/**
	 * 格式化子标题
	 * @param item
	 * @param name
	 * @param moduleType
	 */
	const formatSubTitle = (item: any, name: string, moduleType: ModuleEnum): string => {
		// 图集需拼接图集索引
		if (moduleType === ModuleEnum.standardMap && item.figureNumber) {
			return `${item.figureNumber} / ${item[name]}`;
		}
		// 国家规范无条款编号
		if (moduleType === ModuleEnum.standard && (!item.articleCode || !item.articleCode.trim())) {
			return '暂无编号';
		}
		return item[name];
	}


	/**
	 * 按字段分组
	 * @param list
	 * @param key
	 * @param callback
	 */
	const groupBy = (list: any[], key: string, name: string, moduleType: ModuleEnum, callback: (info: any) => string): any[] => {
		let keys = [];
		(list || []).forEach((v: any) => {
			if (!keys.includes(v[key])) {
				keys.push(v[key]);
			}
		})
		// 分组
		let tempList = keys.map((v: any) => {
			return list.filter((item: any) => item[key] === v);
		})
		// 生成二层树
		let tempTree = tempList.map((v: any) => {
			return {
				key: v[0][key],
				title: callback(v[0]),
				children: v.map((item: any) => ({
					...item,
					key: item.pkid,
					title: formatSubTitle(item, name, moduleType),
					selectable: true,
					moduleType,
				})),
			}
		})

		return tempTree;
	}


	/**
	 * 打开新页面
	 * @param data
	 * @param type
	 */
	const openPage = async (data: any, type: ModuleEnum) => {
		const { pkid, menuId, major } = data;

		let res = null;
		if (type === ModuleEnum.learn) {	// 由于学习资料多归属问题，通过appType, pkid调用接口无法获得准确的路径参数,故此处需要手动注入参数
			let data = { packageId: menuId, pkId: pkid, major };
			res = await getArticleInfo({ appType: type, pkid, data });
		} else {
			res = await getArticleInfo({appType: type, pkid});
		}

		window.open(res.url, '_blank');
	}


	/**
	 * 获取多归属图标
	 * @param data
	 * @param type
	 */
	const getMultipleIcon = (data: any, type: ModuleEnum): React.ReactNode => {
		const { paths } = data;

		let title = '';
		if (type === ModuleEnum.learn) {
			title = '存在于多个学习资料中：';
		}
		if (type === ModuleEnum.rules) {
			title = '本条款存在于以下分类中：';
		}

		if (ObjTest.isNotEmptyArray(paths) && paths.length > 1) {
			return (
				<Tooltip
					overlayClassName="knowledge-tree-multiple-tips"
					placement="bottomLeft"
					align={{ offset: [-10, -5] }}
					title={(
						<div className="tips-list" onClick={(e: any) => e.stopPropagation()}>
							<p className="tips-title">{title}</p>
							{
								(paths || []).map((v: any, i: number) => (
									<p className="tips-item" key={i} onClick={() => openPage(v, type)}>{v.fullPath}</p>
								))
							}
						</div>
					)}
				>
					<MultipleBookIcon className="ml8 multiple-icon" />
				</Tooltip>
			)
		}

		return null;
	}


	/**
	 * 展开/收起全部
	 * @param status true: 展开  false: 收起
	 * @param list
	 */
	const expandHandle = (status: boolean, list: any[]) => {
		let pkids = [];
		if (status && ObjTest.isNotEmptyArray(list)) {
			let target = list.find((v: any) => v.key === 'materialList');
			if (target) {
				pkids = getListFromTree(target.children || []).map((v: any) => v.pkid);
			}
		}
		setExpandedKeys(pkids);
		setExpandAll(status);
	}


	/**
	 * 学子资料选中节点
	 * @param nodeInfo
	 */
	const selectNode = (nodeInfo: any) => {
		const { pkid } = nodeInfo;
		setExpandedKeys(Array.from(new Set([...expandedKeys, pkid])));
		onSelect(nodeInfo);
	}


	/**
	 * 判断是否学习资料
	 * @param item
	 */
	const isLearnData = (item: any): boolean => {
		return item.key === 'materialList';
	}


	return (
		<div className={generateClassName(['knowledge-group-component-wrapper', className])} style={style}>
			{
				ObjTest.isNotEmptyArray(data) ? (
					data.map((v: any) => (
						<div className="mb8 knowledge-section" key={v.key}>
							<div className={generateClassName(['mb8', isLearnData(v) ? 'flexic' : ''])}>
								<span className="knowledge-section-title">{v.title}</span>
								{
									isLearnData(v) && (
										<ExpandButton
											className="mla mr6"
											expanded={expandAll}
											onClick={() => expandHandle(!expandAll, data)}
										/>
									)
								}
							</div>
							{
								// 学习资料
								isLearnData(v) ? (
									<Tree
										className="knowledge-tree"
										showIcon
										blockNode
										defaultExpandAll
										treeData={v.children || []}
										selectedKeys={selectedKeys}
										expandedKeys={expandedKeys}
										onExpand={(keys: string[]) => setExpandedKeys(keys)}
										onSelect={(_, e: any) => selectNode(e.node)}
									/>
								) : (
									// 典型问题
									v.key === 'problemList' ? (
										<div className="knowledge-item">
											<div className="knowledge-label-list">
												{
													(v.children || []).map((subItem: any, k: number) => (
														<span
															key={k}
															className={generateClassName([
																'knowledge-label-item',
																selectedKeys.includes(subItem.key) ? 'active' : ''
															])}
															title={subItem.title}
															onClick={() => onSelect(subItem)}
														>{subItem.title}
																</span>
													))
												}
											</div>
										</div>
									) : (
										(v.children || []).map((item: any, j: number) => (
											<div className="knowledge-item" key={j}>
												<p className="knowledge-title eps" title={item.title}>{item.title}</p>
												<div className="knowledge-label-list">
													{
														(item.children || []).map((subItem: any, k: number) => (
															<span
																key={k}
																className={generateClassName([
																	'knowledge-label-item',
																	selectedKeys.includes(subItem.key) ? 'active' : ''
																])}
																title={subItem.title}
																onClick={() => onSelect(subItem)}
															>{subItem.title}{getMultipleIcon(subItem, v.type)}</span>
														))
													}
												</div>
											</div>
										))
									)

								)
							}
						</div>
					))
				) : (
					<CustomEmpty loading={loading} />
				)
			}
			<Spin className="custom-fixed-loading-wrap" spinning={loading} />
		</div>
	)
}


export default KnowledgeGroup;
